import { DomainForm } from "@/components/domain";
import { DraggableList } from "@/components/draggable";
import { SelectSearch } from "@/components/select-search";
import { Button } from "@/components/ui/button";
import { useGetClients } from "@/hooks/clients";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useGetServiceProvider,
  useGetServiceProviders,
} from "@/hooks/service-providers/get.hooks";
import { useGetService, useGetServices } from "@/hooks/services";
import { T } from "@/i18n";
import {
  CreateReservationService,
  ReservationService,
  reservationServiceStateArray,
} from "@/models";
import { useForm } from "@deadcow-enterprises/react-forms";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Trash } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useReservationFormContext } from "../contex";

export function Activities({
  day,
  services,
  reservationId,
}: {
  services: ReservationService[];
  day: number;
  reservationId: number;
}) {
  const { scheduleManager } = useReservationFormContext();

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          <T tKey="reservations.form.schedule.service.title" />
        </span>
        <UpsertCard
          day={day}
          order={services.length + 1}
          reservationId={reservationId}
          onUpsert={async (model) => {
            scheduleManager.handleAddService(day, model);
          }}
        >
          <Button size="sm" variant="text" className="m-1">
            <Plus />
            <T tKey="reservations.form.schedule.service.new" />
          </Button>
        </UpsertCard>
      </div>
      <div className="grid gap-2">
        <DraggableList
          list={services}
          getId={(s) => s.id}
          onChange={(v) =>
            scheduleManager.handleReorderOfServices(
              day,
              v.map((s) => s.order),
            )
          }
          render={(elem) => (
            <ServiceCard reservationService={elem} key={elem.id} />
          )}
        ></DraggableList>
      </div>
    </div>
  );
}

function ServiceCard({
  reservationService,
}: {
  reservationService: ReservationService;
}) {
  const { t } = useTranslation();
  const { scheduleManager } = useReservationFormContext();
  const { data: clients, isLoading: clientsLoading } = useGetClients();
  const { data: service, isLoading: serviceLoading } = useGetService(
    reservationService.serviceId.toString(),
  );
  const { data: serviceProvider, isLoading: serviceProviderLoading } =
    useGetServiceProvider(
      "service",
      reservationService.serviceProviderId?.toString() ?? "",
      { enabled: typeof reservationService.serviceProviderId === "number" },
    );
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: reservationService.id });

  const hour = useMemo(() => {
    if (reservationService.startHour && reservationService.endHour) {
      return `${reservationService.startHour} - ${reservationService.endHour}`;
    }
    if (reservationService.startHour) {
      return reservationService.startHour;
    }
    if (reservationService.endHour) {
      return reservationService.endHour;
    }
    return undefined;
  }, [reservationService.endHour, reservationService.startHour]);

  return (
    <div
      className="flex items-start gap-2"
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
      }}
    >
      <Button
        type="button"
        className="mt-2 cursor-grab"
        variant="ghost"
        {...attributes}
        {...listeners}
      >
        <GripVertical />
      </Button>
      <Card className="w-full">
        <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
          <div className="grid gap-1">
            {serviceLoading || !service ? (
              <Skeleton className="h-5 w-1/5" />
            ) : (
              <span className="font-semibold">{service.name}</span>
            )}
            <span className="text-primary">{hour ?? "-"}</span>
          </div>
          <div>
            <UpsertCard
              day={reservationService.day}
              order={reservationService.order}
              reservationId={reservationService.reservationId}
              model={reservationService}
              onUpsert={async (model) => {
                scheduleManager.handleUpdateService(reservationService.day, {
                  id: reservationService.id,
                  ...model,
                });
              }}
            >
              <Button variant="ghost" size="icon" className="m-1">
                <Pencil />
                <span className="sr-only">
                  <T tKey="reservations.form.schedule.service.edit" />
                </span>
              </Button>
            </UpsertCard>
            <Button
              variant="text-destructive"
              size="icon"
              className="m-1"
              onClick={() =>
                scheduleManager.handleDeleteService(
                  reservationService.day,
                  reservationService.id,
                )
              }
            >
              <Trash />
              <span className="sr-only">
                <T tKey="reservations.form.schedule.service.delete" />
              </span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-1">
          <span className="break-all pl-1 text-muted-foreground">
            <span className="font-medium text-card-foreground">
              <T tKey="reservations.form.schedule.service.fields.serviceProviderId" />
              {": "}
            </span>
            {serviceProviderLoading || !serviceProvider ? (
              <Skeleton className="h-5 w-1/5" />
            ) : (
              (serviceProvider.provider?.name ?? "-")
            )}
          </span>
          <span className="break-all pl-1 text-muted-foreground">
            <span className="font-medium text-card-foreground">
              <T tKey="reservations.form.schedule.service.fields.price" />
              {": "}
            </span>
            {reservationService.price ?? serviceProvider?.price ?? "-"}
            {" €"}
          </span>
          <span className="break-all pl-1 text-muted-foreground">
            <span className="font-medium text-card-foreground">
              <T tKey="reservations.form.schedule.service.fields.state" />
              {": "}
            </span>
            {reservationService.state
              ? t(`reservations.servicesState.${reservationService.state}`)
              : "-"}
          </span>
          <span className="break-all pl-1 text-muted-foreground">
            <span className="font-medium text-card-foreground">
              <T tKey="reservations.form.schedule.service.fields.notes" />
              {": "}
            </span>
            {reservationService.notes || "-"}
          </span>
          <span className="break-all pl-1 text-muted-foreground">
            <span className="font-medium text-card-foreground">
              <T tKey="reservations.form.schedule.service.fields.clientNotes" />
              {": "}
            </span>
            {reservationService.clientNotes || "-"}
          </span>
          <div className="mt-4 flex justify-between gap-2">
            <span className="font-medium text-primary">
              <T tKey="reservations.form.schedule.service.participants.title" />
            </span>
            <AddClient
              day={reservationService.day}
              serviceId={reservationService.id}
            >
              <Button size="sm" variant="text" className="m-1">
                <Plus />
                <T tKey="reservations.form.schedule.service.participants.add" />
              </Button>
            </AddClient>
          </div>
          {reservationService.reservationClients.map((rc) => (
            <div className="flex items-center gap-2" key={rc.clientId}>
              <Button
                size="sm"
                variant="text-destructive"
                className="m-1"
                onClick={() => {
                  const srv = {
                    ...reservationService,
                    reservationClients:
                      reservationService.reservationClients.filter(
                        (c) => c.clientId !== rc.clientId,
                      ),
                  };
                  scheduleManager.handleUpdateService(srv.day, srv);
                }}
              >
                <Trash />
              </Button>
              <span className="font-medium text-muted-foreground">
                {clientsLoading || !clients ? (
                  <Skeleton className="h-3 w-full" />
                ) : (
                  clients?.find(
                    (c: { id: string }) => c.id === rc.clientId.toString(),
                  )?.name
                )}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function AddClient({
  day,
  children,
  serviceId,
}: {
  day: number;
  children?: ReactNode;
  serviceId: string;
}) {
  const [open, setOpen] = useState(false);
  const { reservationClients, days, scheduleManager } =
    useReservationFormContext();

  const { data: clientsDetails, isLoading: clientsDetailsloading } =
    useGetClients();

  const invalidClientIds = useMemo(() => {
    return (
      days
        .find((d) => d.reservationDay.value.day === day)
        ?.services.filter((a) => a.value.id === serviceId)
        .map((a) => a.value.reservationClients)
        .flat()
        .map((rc) => rc.clientId) ?? []
    );
  }, [serviceId, day, days]);

  const service = useMemo(
    () =>
      days
        .find((d) => d.reservationDay.value.day === day)!
        .services.find((a) => a.value.id === serviceId)!.value,
    [serviceId, day, days],
  );

  const clientsOfDay = useMemo(() => {
    return days
      .find((d) => d.reservationDay.value.day === day)
      ?.services.filter((s) => s.value.id === service.id)
      .map((a) => a.value.reservationClients)
      .flat()
      .map((c) => c.clientId);
  }, [day, days]);

  const clients = useMemo(
    () => reservationClients.filter((c) => !clientsOfDay?.includes(c.clientId)),
    [clientsOfDay, reservationClients],
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="max-h-[520px] w-80 overflow-y-scroll"
        align="end"
        side="left"
      >
        {!clientsDetails || clientsDetailsloading ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <SelectSearch
            options={clients
              .filter((c) => !invalidClientIds.includes(c.clientId))
              .map((c) => ({
                label: clientsDetails.find(
                  (cd) => cd.id === c.clientId.toString(),
                )!.name,
                value: c.clientId,
              }))}
            value={undefined}
            onChange={(v) => {
              const srv = {
                ...service,
                reservationClients: [...service.reservationClients],
              };
              srv.reservationClients.push({
                serviceId: Number.parseInt(serviceId),
                clientId: v,
                reservationId: service.reservationId!,
              });
              scheduleManager.handleUpdateService(day, srv);
              setOpen(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function UpsertCard({
  day,
  order,
  reservationId,
  onUpsert,
  model,
  children,
}: {
  day: number;
  reservationId: number;
  order: number;
  model?: CreateReservationService;
  onUpsert: (v: CreateReservationService) => Promise<void>;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="max-h-[520px] w-80 overflow-y-scroll"
        align="center"
        side="left"
      >
        <Form
          model={model}
          onUpsert={async (v) => {
            await onUpsert(v);
            setOpen(false);
          }}
          day={day}
          order={order}
          reservationId={reservationId}
        />
      </PopoverContent>
    </Popover>
  );
}

function Form({
  day,
  order,
  reservationId,
  onUpsert,
  model,
}: {
  day: number;
  reservationId: number;
  order: number;
  model?: CreateReservationService;
  onUpsert: (v: CreateReservationService) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { data: services, isLoading: servicesLoading } = useGetServices();
  const form = useForm<CreateReservationService>({
    day: {
      value: model?.day ?? day,
    },
    reservationId: {
      value: model?.reservationId ?? reservationId,
    },
    order: {
      value: model?.order ?? order,
    },
    endHour: {
      value: model?.endHour ?? "",
    },
    startHour: {
      value: model?.startHour ?? "",
    },
    serviceId: {
      value: model?.serviceId ?? -1,
      validator: z.number().min(0, "errors.required"),
    },
    serviceProviderId: {
      value: model?.serviceProviderId,
    },
    clientNotes: {
      value: model?.clientNotes ?? "",
    },
    notes: {
      value: model?.notes ?? "",
    },
    price: {
      value: model?.price,
    },
    state: {
      value: model?.state,
    },
    reservationClients: {
      value: model?.reservationClients ?? [],
    },
  });

  return (
    <DomainForm
      form={form}
      onUpsert={onUpsert}
      buttonTranslation={
        model
          ? "reservations.form.schedule.service.update"
          : "reservations.form.schedule.service.create"
      }
      fields={[
        {
          key: "startHour",
          label: "reservations.form.schedule.service.fields.startHour",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.startHour ?? ""}
              type="time"
              onChange={(e) => {
                form.setValue("startHour", e.target.value);
              }}
            />
          ),
        },
        {
          key: "endHour",
          label: "reservations.form.schedule.service.fields.endHour",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.endHour ?? ""}
              type="time"
              onChange={(e) => {
                form.setValue("endHour", e.target.value);
              }}
            />
          ),
        },
        {
          key: "serviceId",
          label: "reservations.form.schedule.service.fields.serviceId",
          input: (form, id) =>
            servicesLoading || !services ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.serviceId ?? undefined}
                onChange={(v) => {
                  form.setValue("serviceId", v);
                }}
                options={services.map((s) => ({ label: s.name, value: s.id }))}
              />
            ),
        },
        {
          key: "serviceProviderId",
          label: "reservations.form.schedule.service.fields.serviceProviderId",
          input: (form, id) => (
            <ServiceProviderSearch
              id={id}
              serviceProviderId={form.value.serviceProviderId?.toString()}
              serviceId={form.value.serviceId}
              onChange={(v, price) => {
                form.setValue("serviceProviderId", Number.parseInt(v));
                form.setValue("price", price);
              }}
            />
          ),
        },
        {
          key: "price",
          label: "reservations.form.schedule.service.fields.price",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.price ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue("price", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          key: "state",
          label: "reservations.form.schedule.service.fields.state",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.state}
              onChange={(v) => {
                form.setValue("state", v);
              }}
              options={reservationServiceStateArray.map((v) => ({
                value: v,
                label: t(`reservations.servicesState.${v}`),
              }))}
            />
          ),
        },
        {
          key: "notes",
          label: "reservations.form.schedule.service.fields.notes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.notes ?? ""}
              onChange={(e) => {
                form.setValue("notes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "clientNotes",
          label: "reservations.form.schedule.service.fields.clientNotes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.clientNotes ?? ""}
              onChange={(e) => {
                form.setValue("clientNotes", e.target.value);
              }}
            />
          ),
        },
      ]}
    />
  );
}

function ServiceProviderSearch({
  id,
  serviceId,
  serviceProviderId,
  onChange,
}: {
  id: string;
  serviceProviderId: string | null | undefined;
  serviceId: number;
  onChange: (v: string, price: number | null | undefined) => void;
}) {
  const { data: serviceProviders, isLoading: serviceProvidersLoading } =
    useGetServiceProviders("service", serviceId.toString(), {
      enabled: serviceId >= 0,
    });

  if (serviceProvidersLoading || !serviceProviders) {
    return <Skeleton className="h-9 w-full" />;
  }

  return (
    <SelectSearch
      id={id}
      className="w-full"
      value={serviceProviderId ?? undefined}
      onChange={(v) => {
        const price = serviceProviders.find((s) => s.id === v)?.price;
        onChange(v, price);
      }}
      options={serviceProviders.map((s) => ({
        label: s.provider?.name ?? "-",
        value: s.id,
      }))}
    />
  );
}
